import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Mock temporário para substituir as antigas APIs
const mockDocuments = [
  {
    id: 1,
    title: 'Manual do Usuário v2.1',
    fileName: 'manual_usuario_v2.1.pdf',
    chatbot: 'suporte',
    status: 'indexed',
    indexedAt: '2024-02-15 10:30',
    vectorCount: 1250,
    fileSize: '2.5 MB',
    lastAccessed: '2024-02-15 14:20'
  },
  {
    id: 2,
    title: 'Política de Segurança',
    fileName: 'politica_seguranca.pdf',
    chatbot: 'documentos',
    status: 'indexed',
    indexedAt: '2024-02-14 16:45',
    vectorCount: 890,
    fileSize: '1.8 MB',
    lastAccessed: '2024-02-15 11:15'
  },
  {
    id: 3,
    title: 'Procedimentos Operacionais',
    fileName: 'procedimentos_operacionais.docx',
    chatbot: 'treinamento',
    status: 'processing',
    indexedAt: '2024-02-15 09:00',
    vectorCount: 0,
    fileSize: '3.2 MB',
    lastAccessed: null
  },
  {
    id: 4,
    title: 'Relatório Anual 2023',
    fileName: 'relatorio_anual_2023.pdf',
    chatbot: 'documentos',
    status: 'error',
    indexedAt: null,
    vectorCount: 0,
    fileSize: '5.1 MB',
    lastAccessed: null
  }
];

export const ChatbotDocumentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState(mockDocuments);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [chatbotFilter, setChatbotFilter] = useState('all');

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.fileName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    const matchesChatbot = chatbotFilter === 'all' || doc.chatbot === chatbotFilter;
    return matchesSearch && matchesStatus && matchesChatbot;
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      indexed: { color: 'bg-green-100 text-green-800', label: 'Indexado', icon: '✅' },
      processing: { color: 'bg-yellow-100 text-yellow-800', label: 'Processando', icon: '⏳' },
      error: { color: 'bg-red-100 text-red-800', label: 'Erro', icon: '❌' },
      pending: { color: 'bg-gray-100 text-gray-800', label: 'Pendente', icon: '⏸️' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getChatbotBadge = (chatbot: string) => {
    const chatbotConfig = {
      suporte: { color: 'bg-blue-100 text-blue-800', label: 'Suporte' },
      documentos: { color: 'bg-purple-100 text-purple-800', label: 'Documentos' },
      treinamento: { color: 'bg-green-100 text-green-800', label: 'Treinamento' }
    };
    
    const config = chatbotConfig[chatbot as keyof typeof chatbotConfig] || { color: 'bg-gray-100 text-gray-800', label: chatbot };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleRemoveDocument = (documentId: number) => {
    if (window.confirm('Tem certeza que deseja remover este documento?')) {
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-blue-900 mb-2">
          Documentos Indexados
        </h1>
        <p className="text-gray-600">
          Gerencie os documentos que foram indexados para os chatbots.
        </p>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos os status</option>
            <option value="indexed">Indexado</option>
            <option value="processing">Processando</option>
            <option value="error">Erro</option>
            <option value="pending">Pendente</option>
          </select>
        </div>
        <div className="sm:w-48">
          <select
            value={chatbotFilter}
            onChange={(e) => setChatbotFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todos os chatbots</option>
            <option value="suporte">Suporte</option>
            <option value="documentos">Documentos</option>
            <option value="treinamento">Treinamento</option>
          </select>
        </div>
      </div>

      {/* Lista de Documentos */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredDocuments.map((document) => (
            <li key={document.id}>
              <div className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {document.title}
                        </h3>
                        {getStatusBadge(document.status)}
                        {getChatbotBadge(document.chatbot)}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {document.fileName}
                      </p>
                      <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                        <span>Tamanho: {document.fileSize}</span>
                        {document.vectorCount > 0 && (
                          <span>Vetores: {document.vectorCount.toLocaleString()}</span>
                        )}
                        {document.indexedAt && (
                          <span>Indexado em: {document.indexedAt}</span>
                        )}
                        {document.lastAccessed && (
                          <span>Último acesso: {document.lastAccessed}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                      Detalhes
                    </button>
                    <button 
                      onClick={() => handleRemoveDocument(document.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Remover
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Mensagem quando não há documentos */}
      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum documento encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Tente ajustar os filtros ou adicionar novos documentos.
          </p>
        </div>
      )}
    </div>
  );
};
